/**
 * useInterstitialAd hook — shows interstitial ads on native platforms.
 * Enforces a 3-minute cooldown between ads to prevent user frustration.
 */

import { useCallback, useRef } from "react";
import { shouldShowAds } from "../utils/ads";
import { useAuth } from "../contexts/AuthContext";
import { getInterstitialId, getPlatform } from "../utils/adConfig";

const COOLDOWN_MS = 3 * 60 * 1000; // 3 minutes
let lastShownAt = 0;

export default function useInterstitialAd() {
  const { dbUser } = useAuth();
  const loadingRef = useRef(false);

  const showInterstitial = useCallback(async () => {
    if (!shouldShowAds(dbUser)) return;
    if (Date.now() - lastShownAt < COOLDOWN_MS) return;
    if (loadingRef.current) return;

    try {
      const platform = await getPlatform();
      if (platform === "web") return;

      const { AdMob } = await import("@capacitor-community/admob");
      const adId = await getInterstitialId();

      loadingRef.current = true;
      await AdMob.prepareInterstitial({ adId });
      await AdMob.showInterstitial();
      lastShownAt = Date.now();
    } catch (err) {
      console.error("Interstitial error:", err);
    } finally {
      loadingRef.current = false;
    }
  }, [dbUser]);

  return { showInterstitial };
}
